import { Guid } from "@/utils/guid"
import { getFormatMessage } from "@/utils/utils"
import { CheckboxOptionType } from "antd"

const formatMessage = getFormatMessage()

export interface MqttCombination {
  id?: string
  name?: string
  format?: string
  description?: string
  isDefault?: boolean
}

export interface MqttTimestamp {
  id?: string
  name?: string
  format?: string
  zone?: string
  description?: string
  isDefault?: boolean
  enable?: boolean
}

export interface MqttProtocol {
  key?: string
  name?: string
}

export interface MqttVariable {
  key?: string
  name?: string
  dataType?: number
  description?: string
}

/**
 * MQTT内置组合方式
 */
export const defaultMqttCombinations: MqttCombination[] = [
  {
    id: Guid.raw(),
    name: 'KV',
    format: `{"%name%":"%value%"}`,
    description: 'System',
    isDefault: true,
  },
  {
    id: Guid.raw(),
    name: 'NV',
    format: `{"name":"%name%","value":"%value%"}`,
    description: 'System',
    isDefault: true,
  },
  {
    id: Guid.raw(),
    name: 'Ali',
    format: `{"%name%":{"value":"%value%"}}`,
    description: 'System',
    isDefault: true,
  },
  {
    id: Guid.raw(),
    name: 'OneNet',
    format: `{"%name%":[{"v":"%value%"}]}`,
    description: 'System',
    isDefault: true,
  },
]

/**
 * 默认内置时间格式
 */
export const defaultMqttTimestamps: MqttTimestamp[] = [
  {
    id: Guid.raw(),
    name: 'Time',
    format: 'unix-long-s-utc',
    zone: 'utc',
    description: 'System',
    isDefault: true,
    enable: true
  }
]

/**
 * MQTT平台协议
 */
export const defaultMqttProtocols: MqttProtocol[] = [
  {
    key: 'flexem',
    name: formatMessage({ id: 'app.mqtt.platform.flexem' })
  },
  {
    key: 'ali_plat',
    name: formatMessage({ id: 'app.mqtt.platform.ali_plat' })
  },
  {
    key: 'huawei_plat',
    name: formatMessage({ id: 'app.mqtt.platform.huawei_plat' })
  },
  {
    key: 'onenet_plat',
    name: formatMessage({ id: 'app.mqtt.platform.onenet_plat' })
  },
  {
    key: 'tencent_plat',
    name: formatMessage({ id: 'app.mqtt.platform.tencent_plat' })
  }
]

/**
 * MQTT内置变量列表
 */
export const mqttVariables = {
  flexem_timestamp: {
    key: Guid.raw(),
    name: 'flexem_timestamp',
    dataType: 999,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_timestamp.desc' })
  } as MqttVariable,
  flexem_message_id: {
    key: Guid.raw(),
    name: 'flexem_message_id',
    dataType: 999,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_message_id.desc' })
  } as MqttVariable,
  flexem_alarm: {
    key: Guid.raw(),
    name: 'flexem_alarm',
    dataType: 991,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_alarm.desc' })
  } as MqttVariable,
  flexem_message: {
    key: Guid.raw(),
    name: 'flexem_message',
    dataType: 995,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_message.desc' })
  } as MqttVariable,
  flexem_error_code: {
    key: Guid.raw(),
    name: 'flexem_error_code',
    dataType: 998,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_error_code.desc' })
  } as MqttVariable,
  gy_deviceId: {
    key: Guid.raw(),
    name: 'gy_deviceId',
    dataType: 995,
    description: formatMessage({ id: 'app.mqtt.variable.gy_deviceId.desc' })
  } as MqttVariable,
  gy_error_code: {
    key: Guid.raw(),
    name: 'gy_error_code',
    dataType: 995,
    description: formatMessage({ id: 'app.mqtt.variable.gy_error_code.desc' })
  } as MqttVariable,
  flexem_info_type: {
    key: Guid.raw(),
    name: 'flexem_info_type',
    dataType: 995,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_info_type.desc' })
  } as MqttVariable,
  flexem_sim_lac: {
    key: Guid.raw(),
    name: 'flexem_sim_lac',
    dataType: 997,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_sim_lac.desc' })
  } as MqttVariable,
  flexem_sim_cel: {
    key: Guid.raw(),
    name: 'flexem_sim_cel',
    dataType: 997,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_sim_cel.desc' })
  } as MqttVariable,
  flexem_sim_mcc: {
    key: Guid.raw(),
    name: 'flexem_sim_mcc',
    dataType: 997,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_sim_mcc.desc' })
  } as MqttVariable,
  flexem_sim_mnc: {
    key: Guid.raw(),
    name: 'flexem_sim_mnc',
    dataType: 997,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_sim_mnc.desc' })
  } as MqttVariable,
  flexem_sim_ccid: {
    key: Guid.raw(),
    name: 'flexem_sim_ccid',
    dataType: 997,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_sim_ccid.desc' })
  } as MqttVariable,
  flexem_longitude: {
    key: Guid.raw(),
    name: 'flexem_longitude',
    dataType: 996,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_longitude.desc' })
  } as MqttVariable,
  flexem_latitude: {
    key: Guid.raw(),
    name: 'flexem_latitude',
    dataType: 996,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_latitude.desc' })
  } as MqttVariable,
  flexem_push_interval: {
    key: Guid.raw(),
    name: 'flexem_push_interval',
    dataType: 994,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_push_interval.desc' })
  } as MqttVariable,
  flexem_push_mode: {
    key: Guid.raw(),
    name: 'flexem_push_mode',
    dataType: 995,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_push_mode.desc' })
  } as MqttVariable,
  flexem_pause: {
    key: Guid.raw(),
    name: 'flexem_pause',
    dataType: 992,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_pause.desc' })
  } as MqttVariable,
  flexem_fcs_ver: {
    key: Guid.raw(),
    name: 'flexem_fcs_ver',
    dataType: 997,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_fcs_ver.desc' })
  } as MqttVariable,
  flexem_floader_ver: {
    key: Guid.raw(),
    name: 'flexem_floader_ver',
    dataType: 997,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_floader_ver.desc' })
  } as MqttVariable,
  flexem_fds_ver: {
    key: Guid.raw(),
    name: 'flexem_fds_ver',
    dataType: 997,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_fds_ver.desc' })
  } as MqttVariable,
  flexem_mqtt_ver: {
    key: Guid.raw(),
    name: 'flexem_mqtt_ver',
    dataType: 997,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_mqtt_ver.desc' })
  } as MqttVariable,
  flexem_sn: {
    key: Guid.raw(),
    name: 'flexem_sn',
    dataType: 995,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_sn.desc' })
  } as MqttVariable,
  flexem_password: {
    key: Guid.raw(),
    name: 'flexem_password',
    dataType: 995,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_password.desc' })
  } as MqttVariable,
  flexem_online_sta: {
    key: Guid.raw(),
    name: 'flexem_online_sta',
    dataType: 993,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_online_sta.desc' })
  } as MqttVariable,
  flexem_net_type: {
    key: Guid.raw(),
    name: 'flexem_net_type',
    dataType: 993,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_net_type.desc' })
  } as MqttVariable,
  flexem_wireless: {
    key: Guid.raw(),
    name: 'flexem_wireless',
    dataType: 993,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_wireless.desc' })
  } as MqttVariable,
  flexem_vnc_op_password: {
    key: Guid.raw(),
    name: 'flexem_vnc_op_password',
    dataType: 995,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_vnc_op_password.desc' })
  } as MqttVariable,
  flexem_vnc_mo_password: {
    key: Guid.raw(),
    name: 'flexem_vnc_mo_password',
    dataType: 995,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_vnc_mo_password.desc' })
  } as MqttVariable,
  flexem_connect_sta: {
    key: Guid.raw(),
    name: 'flexem_connect_sta',
    dataType: 992,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_connect_sta.desc' })
  } as MqttVariable,
  flexem_sd_card_sta: {
    key: Guid.raw(),
    name: 'flexem_sd_card_sta',
    dataType: 992,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_sd_card_sta.desc' })
  } as MqttVariable,
  flexem_usb_sta: {
    key: Guid.raw(),
    name: 'flexem_usb_sta',
    dataType: 992,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_usb_sta.desc' })
  } as MqttVariable,
  flexem_usb_down_line_sta: {
    key: Guid.raw(),
    name: 'flexem_usb_down_line_sta',
    dataType: 992,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_usb_down_line_sta.desc' })
  } as MqttVariable,
  flexem_picture_base64: {
    key: Guid.raw(),
    name: 'flexem_picture_base64',
    dataType: 995,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_picture_base64.desc' })
  } as MqttVariable,
  flexem_reboot: {
    key: Guid.raw(),
    name: 'flexem_reboot',
    dataType: 992,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_reboot.desc' })
  } as MqttVariable,
  flexem_push_alarm: {
    key: Guid.raw(),
    name: 'flexem_push_alarm',
    dataType: 992,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_push_alarm.desc' })
  } as MqttVariable,
  flexem_get_all_items: {
    key: Guid.raw(),
    name: 'flexem_get_all_items',
    dataType: 992,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_get_all_items.desc' })
  } as MqttVariable,
  flexem_get_info: {
    key: Guid.raw(),
    name: 'flexem_get_info',
    dataType: 995,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_get_info.desc' })
  } as MqttVariable,
  flexem_message_id1: {
    key: Guid.raw(),
    name: 'flexem_message_id1',
    dataType: 999,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_message_id.desc' })
  } as MqttVariable,
  flexem_message_id2: {
    key: Guid.raw(),
    name: 'flexem_message_id2',
    dataType: 999,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_message_id.desc' })
  } as MqttVariable,
  flexem_uuid: {
    key: Guid.raw(),
    name: 'flexem_uuid',
    dataType: 995,
    description: formatMessage({ id: 'app.mqtt.variable.flexem_uuid.desc' })
  } as MqttVariable,
}

/**
 * MQTT功能对应内置变量列表
 */
export const mqttFunctionVariables = {
  PushMonitorData: {
    pub: [
      mqttVariables.flexem_timestamp,
      mqttVariables.flexem_message_id,
    ],
    sub: [
      mqttVariables.flexem_message_id,
    ]
  },
  PushAlarm: {
    pub: [
      mqttVariables.flexem_timestamp,
      mqttVariables.flexem_message_id,
      mqttVariables.flexem_alarm,
    ],
    sub: [
      mqttVariables.flexem_message_id,
    ]
  },
  WriteItem: {
    pub: [
      mqttVariables.flexem_timestamp,
      mqttVariables.flexem_message_id,
      mqttVariables.flexem_message,
      mqttVariables.flexem_error_code,
      mqttVariables.gy_deviceId,
      mqttVariables.gy_error_code,
    ],
    sub: [
      mqttVariables.flexem_timestamp,
      mqttVariables.flexem_message_id,
    ]
  },
  Suspend: {
    pub: [
      mqttVariables.flexem_timestamp,
      mqttVariables.flexem_message_id,
      mqttVariables.flexem_message,
      mqttVariables.flexem_error_code,
    ],
    sub: [
      mqttVariables.flexem_timestamp,
      mqttVariables.flexem_message_id,
      mqttVariables.flexem_pause,
    ]
  },
  SetReboot: {
    pub: [],
    sub: [
      mqttVariables.flexem_timestamp,
      mqttVariables.flexem_message_id,
      mqttVariables.flexem_reboot,
    ]
  },
  SetPushMethod: {
    pub: [
      mqttVariables.flexem_timestamp,
      mqttVariables.flexem_message_id,
      mqttVariables.flexem_message,
      mqttVariables.flexem_error_code,
    ],
    sub: [
      mqttVariables.flexem_timestamp,
      mqttVariables.flexem_message_id,
      mqttVariables.flexem_push_interval,
      mqttVariables.flexem_push_mode,
      mqttVariables.flexem_push_alarm,
    ]
  },
  ReadItem: {
    pub: [
      mqttVariables.flexem_timestamp,
      mqttVariables.flexem_message_id,
    ],
    sub: [
      mqttVariables.flexem_timestamp,
      mqttVariables.flexem_message_id,
      mqttVariables.flexem_get_all_items,
    ]
  },
  GetFBoxInfo: {
    pub: [
      mqttVariables.flexem_timestamp,
      mqttVariables.flexem_message_id,
      mqttVariables.flexem_error_code,
      mqttVariables.flexem_info_type,
      mqttVariables.flexem_sim_lac,
      mqttVariables.flexem_sim_cel,
      mqttVariables.flexem_sim_mcc,
      mqttVariables.flexem_sim_mnc,
      mqttVariables.flexem_sim_ccid,
      mqttVariables.flexem_longitude,
      mqttVariables.flexem_latitude,
      mqttVariables.flexem_push_interval,
      mqttVariables.flexem_push_mode,
      mqttVariables.flexem_pause,
      mqttVariables.flexem_fcs_ver,
      mqttVariables.flexem_floader_ver,
      mqttVariables.flexem_fds_ver,
      mqttVariables.flexem_mqtt_ver,
      mqttVariables.flexem_sn,
      mqttVariables.flexem_password,
      mqttVariables.flexem_online_sta,
      mqttVariables.flexem_net_type,
      mqttVariables.flexem_wireless,
      mqttVariables.flexem_vnc_op_password,
      mqttVariables.flexem_vnc_mo_password,
      mqttVariables.flexem_connect_sta,
      mqttVariables.flexem_sd_card_sta,
      mqttVariables.flexem_usb_sta,
      mqttVariables.flexem_usb_down_line_sta,
    ],
    sub: [
      mqttVariables.flexem_timestamp,
      mqttVariables.flexem_message_id,
      mqttVariables.flexem_get_info,
    ]
  },
  GetPicture: {
    pub: [
      mqttVariables.flexem_timestamp,
      mqttVariables.flexem_message,
      mqttVariables.flexem_error_code,
      mqttVariables.flexem_picture_base64,
    ],
    sub: [
      mqttVariables.flexem_timestamp,
      mqttVariables.flexem_message_id,
    ]
  },
  PushAlarmEvent: {
    pub: [
      mqttVariables.flexem_timestamp,
      mqttVariables.flexem_message_id,
      mqttVariables.flexem_message_id1,
      mqttVariables.flexem_message_id2,
      mqttVariables.flexem_uuid,
    ],
    sub: [
      mqttVariables.flexem_uuid,
    ]
  }
}

// 需要显示导入的监控点列表的MQTT功能
const mqttVisibleFunctionItems = [
  'PushMonitorData:pub',
  'PushMonitorData:sub',
  'PushAlarm:pub',
  'PushAlarm:sub',
  'WriteItem:pub',
  'WriteItem:sub',
  'ReadItem:pub',
  'ReadItem:sub',
  'GetPicture:pub',
  'GetPicture:sub',
  'PushAlarmEvent:pub',
  'PushAlarmEvent:sub',
]

/**
 * 判断哪些功能需要显示导入的监控点列表
 */
export const mqttVisibleFunction = {
  includes: (funcName: string, topicType: string) => {
    return mqttVisibleFunctionItems.includes(`${funcName}:${topicType}`)
  }
}

/** 应用模块 */
export const appModules: CheckboxOptionType[] = [
  {
    label: 'PLC和驱动',
    value: 'plcs',
  },
  {
    label: '监控点条目',
    value: 'variables',
  },
  {
    label: '报警条目',
    value: 'alarms',
  },
  {
    label: '边缘计算条目',
    value: 'macros',
  },
  {
    label: '转发配置',
    value: 'transfers',
  }
]

/** 默认模块 */
export const defaultAppModules: string[] = appModules.map(x => x.value as string)

export const secureConnectionTypes = ['ssl://', 'wss://']

export const variableBatchLimit = [1, 40]
export const variableBatchIncrementLimit = [-200, 200]
